import { execute } from '@/graphql/execute';
import { executeSenhub } from '@/graphql/executeSenhub';
import { AuditLogAction, AuditLogEntityType } from '@/models/audit-log.models';
import { createAuditLogInput } from '@/utils/audit-log.function';
import type {
  CustomerSecretOperation,
  CustomerSecretTarget,
} from './customerSecretStore';
import {
  deleteCustomerSecretSenhubMutation,
  insertCustomerSecretAuditLogMutation,
  upsertCustomerSecretSenhubMutation,
} from './graphql/senhubSecretMutation';

export type CustomerSecretSaveResult = {
  configValues: Record<string, string>;
  // Saved in senhub, but the audit log row could not be written.
  auditFailures: CustomerSecretTarget[];
};

// Carries the operations completed before the failure, so the caller can still
// point config at them.
export class CustomerSecretSaveError
  extends Error
  implements CustomerSecretSaveResult
{
  readonly target: CustomerSecretTarget;
  readonly cause: unknown;
  readonly configValues: Record<string, string>;
  readonly auditFailures: CustomerSecretTarget[];

  constructor(
    target: CustomerSecretTarget,
    cause: unknown,
    completed: CustomerSecretSaveResult,
  ) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'CustomerSecretSaveError';
    this.target = target;
    this.cause = cause;
    this.configValues = completed.configValues;
    this.auditFailures = completed.auditFailures;
  }
}

// Secret values go to senhub only; Hasura gets the audit row, never secretJson.
// A senhub failure stops the loop; an audit failure is recorded and the loop goes on.
export async function saveCustomerSecretOperations(
  customerId: string,
  operations: readonly CustomerSecretOperation[],
): Promise<CustomerSecretSaveResult> {
  const result: CustomerSecretSaveResult = {
    configValues: {},
    auditFailures: [],
  };

  for (const { target, secretJson } of operations) {
    try {
      const data = await executeSenhub({
        query: upsertCustomerSecretSenhubMutation,
        variables: {
          input: { customerId, pathTail: target.pathTail, secretJson },
          invalidateCacheInput: { customerId, pathTail: target.pathTail },
        },
      });
      result.configValues[target.id] = data.upsertCustomerSecret.uri;
    } catch (error) {
      throw new CustomerSecretSaveError(target, error, result);
    }

    if (
      !(await writeAuditLog(customerId, AuditLogAction.UPSERT_CUSTOMER_SECRET))
    ) {
      result.auditFailures.push(target);
    }
  }

  return result;
}

export type CustomerSecretDeleteResult = {
  // Nothing was stored there, so the key wasn't created by the console.
  leftInPlace: CustomerSecretTarget[];
  // The delete failed and the key stays in the vault, unreferenced.
  failed: CustomerSecretTarget[];
  auditFailures: CustomerSecretTarget[];
};

// For keys config no longer references. A failure only leaves an unused key
// behind, so every delete is attempted.
export async function deleteCustomerSecrets(
  customerId: string,
  targets: readonly CustomerSecretTarget[],
): Promise<CustomerSecretDeleteResult> {
  const result: CustomerSecretDeleteResult = {
    leftInPlace: [],
    failed: [],
    auditFailures: [],
  };

  for (const target of targets) {
    try {
      const data = await executeSenhub({
        query: deleteCustomerSecretSenhubMutation,
        variables: { input: { customerId, pathTail: target.pathTail } },
      });
      if (!data.deleteCustomerSecret.deleted) {
        result.leftInPlace.push(target);
      }
    } catch {
      result.failed.push(target);
      continue;
    }

    if (
      !(await writeAuditLog(customerId, AuditLogAction.DELETE_CUSTOMER_SECRET))
    ) {
      result.auditFailures.push(target);
    }
  }

  return result;
}

async function writeAuditLog(
  customerId: string,
  action: AuditLogAction,
): Promise<boolean> {
  try {
    await execute({
      query: insertCustomerSecretAuditLogMutation,
      variables: {
        auditLogInput: createAuditLogInput({
          action,
          entities: [{ id: customerId, entity: AuditLogEntityType.CUSTOMER }],
        }),
      },
    });
    return true;
  } catch {
    return false;
  }
}
